import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { RequirementsProps } from '../../../tools/context';
import { useTranslation } from '../../../tools/i18n';
import { CustomDataStore, CustomStoreGroup } from '../../../tools/providersAPI';
import ContainerShopV2 from '../Container';
import LabelsShopV2 from '../Labels';
import DetailsStoreItemComp from './Details';
import TableStoreItemComp from './Table';
import TextStoreItemComp from './Text';
import TextListStoreItemComp from './TextList';

export interface GroupsShopV2Props {
  seller: { [key: string]: any };
  data: { [key: string]: any };
  customData: CustomDataStore;
  requirements: RequirementsProps;
}

interface RenderableGroup {
  key: string;
  anchorId: string;
  title: string;
  summary: string;
  isLabels: boolean;
  items?: any[];
}

const GroupsShopV2: React.FC<GroupsShopV2Props> = ({ seller, data, customData, requirements }) => {
  const { t } = useTranslation();
  const { groups } = customData;
  const [openSectionKey, setOpenSectionKey] = useState<string | null>(null);

  const addGroupAnchors = (groups: CustomStoreGroup[]) => {
    if (!groups?.length) return [];
    const updatedGroups = [...groups];
    if (customData?.store?.labels || customData?.store?.handicaps) {
      updatedGroups.push({ anchor: 'labels' });
    }
    return updatedGroups;
  };

  const getSortedGroups = (groups: CustomStoreGroup[]) => {
    const order = [
      'schedules', // Périodes d'ouverture et horaires
      'prices', // Tarifs
      'transport', // Transport
      'paymentMethods', // Moyens de paiement
      'equipmentsAndServices', // Équipements et services
      'capacite', // Capacité (si présent)
      'visits', // Visites
      'activities', // Activités
      'labels', // Classements et labels
      'spokenLanguages', // Langues parlées
    ];

    const getOrderKey = (group: CustomStoreGroup) => {
      if (group?.anchor) return group.anchor;
      const rawTitle = (group as any)?.title;
      if (typeof rawTitle === 'string') {
        const match = rawTitle.match(/^\{\{t:(.*?)\}\}$/);
        if (match?.[1]) return match[1];
        // e.g. "Transport" (avoid referencing `slugify` before initialization)
        return rawTitle
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }
      return '';
    };

    return [...groups.filter((group) => group.title !== '{{t:socialNetworks}}')]
      .map((group, index) => ({ group, index }))
      .sort((a, b) => {
        const keyA = getOrderKey(a.group);
        const keyB = getOrderKey(b.group);
        const rankA = order.indexOf(keyA);
        const rankB = order.indexOf(keyB);
        const scoreA = rankA === -1 ? Infinity : rankA;
        const scoreB = rankB === -1 ? Infinity : rankB;
        if (scoreA !== scoreB) return scoreA - scoreB;
        // Stable fallback: keep initial order for unknown/unranked groups
        return a.index - b.index;
      })
      .map(({ group }) => group);
  };

  const augmentedGroups = useMemo(
    () => getSortedGroups(addGroupAnchors(groups)),
    [groups, customData?.store?.labels, customData?.store?.handicaps]
  );

  const groupTypes = {
    text: TextStoreItemComp,
    textList: TextListStoreItemComp,
    details: DetailsStoreItemComp,
    table: TableStoreItemComp,
  };

  const slugify = (value: string) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const stripHtml = (value: string) => value?.replace(/<[^>]*>/g, '')?.trim();

  const getGroupTitle = (group: any) =>
    group.title?.replace(/{{t:(.*?)}}/g, (match, key) => t(`shopV2.groupsCustom.titles.${key}`));

  const getSummaryLabel = (group: any, title: string, count: number) => {
    const normalizedTitle = slugify(title || '');
    if (group.anchor === 'labels' || normalizedTitle.includes('classements-et-labels')) {
      return count > 1 ? 'classements et labels' : 'classement et label';
    }
    if (
      normalizedTitle.includes('equipements-et-services') ||
      normalizedTitle.includes('equipement-et-service')
    ) {
      return count > 1 ? 'équipements et services' : 'équipement et service';
    }
    if (normalizedTitle.includes('langues-parlees')) {
      return count > 1 ? 'langues parlées' : 'langue parlée';
    }
    if (normalizedTitle.includes('moyens-de-paiement')) {
      return count > 1 ? 'moyens de paiement' : 'moyen de paiement';
    }
    if (normalizedTitle.includes('environnement')) {
      return count > 1 ? "éléments d'environnement" : "élément d'environnement";
    }
    if (normalizedTitle.includes('themes')) {
      return count > 1 ? 'thèmes' : 'thème';
    }
    return count > 1 ? 'éléments' : 'élément';
  };

  const getTextListSummary = (texts: string[] = []) => {
    if (!texts.length) return '';
    const firstLine = stripHtml(texts[0] ?? '');
    const count = texts.length;
    const label = count > 1 ? 'lignes' : 'ligne';
    return `${count} ${label}${firstLine ? ` - ${firstLine}` : ''}`;
  };

  const getPriceSummary = (items: any[] = []) => {
    const tableItem = items.find((item) => item?.type === 'table');
    const tableRows = tableItem?.rows ?? [];
    const priceValues = tableRows
      .flatMap((row: string[]) => row)
      .map((cell: string) => stripHtml(cell ?? ''))
      .join(' ');
    const priceMatch = priceValues.match(/\d+[.,]?\d*\s?€/);
    return priceMatch ? `À partir de ${priceMatch[0]}` : 'Tarifs disponibles';
  };

  const getVisitsSummary = (items: any[] = []) => {
    const tableItem = items.find((item) => item?.type === 'table');
    const tableRows = tableItem?.rows ?? [];
    const languageRow = tableRows.find((row: string[] = []) =>
      stripHtml(row?.[0] ?? '').toLowerCase().includes('langue')
    );
    const languageValue = stripHtml(languageRow?.[1] ?? '');
    return languageValue || 'Informations de visite';
  };

  const getGroupSummary = (group: any, title: string) => {
    if (group.anchor === 'schedules') {
      const textListItem = group.items?.find((item) => item?.type === 'textList');
      return getTextListSummary(textListItem?.texts ?? []);
    }
    if (group.anchor === 'prices') {
      return getPriceSummary(group.items ?? []);
    }
    if (group.anchor === 'visits') {
      return getVisitsSummary(group.items ?? []);
    }
    const detailsItem = group.items?.find((item) => item?.type === 'details');
    if (detailsItem?.details?.length) {
      return `${detailsItem.details.length} ${getSummaryLabel(group, title, detailsItem.details.length)}`;
    }
    const textListItem = group.items?.find((item) => item?.type === 'textList');
    if (textListItem?.texts?.length) {
      return `${textListItem.texts.length} ${getSummaryLabel(group, title, textListItem.texts.length)}`;
    }
    return title ? 'Voir le détail' : '';
  };

  const renderableGroups = useMemo<RenderableGroup[]>(() => {
    return augmentedGroups.reduce((acc: RenderableGroup[], group: any, index: number) => {
      const isLabelsGroup = group.anchor === 'labels';
      if (isLabelsGroup && !customData?.store?.labels.length && !customData?.store?.handicaps.length) {
        return acc;
      }
      if (!isLabelsGroup && !group.items?.length) {
        return acc;
      }
      const title = isLabelsGroup
        ? t(`shopV2.groupsCustom.titles.${group.anchor}`)
        : getGroupTitle(group);
      const anchorBase = group.anchor || slugify(title || `section-${index + 1}`);
      const anchorId = `${anchorBase}-${index + 1}`;
      acc.push({
        key: `${anchorBase}-${index}`,
        anchorId,
        title,
        summary: getGroupSummary(group, title),
        isLabels: isLabelsGroup,
        items: group.items,
      });
      return acc;
    }, []);
  }, [augmentedGroups, customData?.store?.labels, customData?.store?.handicaps, t]);

  useEffect(() => {
    if (!renderableGroups.length) {
      setOpenSectionKey(null);
      return;
    }
    setOpenSectionKey((prev) => {
      if (prev && renderableGroups.some((group) => group.key === prev)) {
        return prev;
      }
      return renderableGroups[0].key;
    });
  }, [renderableGroups]);

  const renderGroupContent = (group: RenderableGroup) => {
    if (group.isLabels) {
      return (
        <LabelsShopV2
          requirements={requirements}
          labels={customData?.store?.labels}
          handicaps={customData?.store?.handicaps}
        />
      );
    }
    return (
      <div className="GroupsShopV2__items">
        {group.items?.map((item: any, i: number) => {
          if (!item?.type) return null;
          const Component = groupTypes[item.type] || null;
          return Component ? <Component key={`${item.type}_${i}`} {...item} /> : null;
        })}
      </div>
    );
  };

  return (
    <Fragment>
      {renderableGroups.map((group, index) => {
        const isOpen = openSectionKey === group.key;
        const triggerId = `${group.anchorId}-trigger`;
        const panelId = `${group.anchorId}-panel`;
        return (
          <ContainerShopV2
            key={group.key}
            id={group.anchorId}
            className={`ContainerShopV2--accordion ${isOpen ? 'ContainerShopV2--accordionOpen' : ''}`}
          >
            <button
              id={triggerId}
              type="button"
              className="ContainerShopV2__accordionTrigger"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpenSectionKey((prev) => (prev === group.key ? null : group.key))}
            >
              <span className="ContainerShopV2__accordionHeader">
                <span className="ContainerShopV2__title">{group.title}</span>
                {!isOpen && group.summary ? (
                  <span className="ContainerShopV2__accordionSummary">{group.summary}</span>
                ) : null}
              </span>
              <span className="ContainerShopV2__accordionIcon" aria-hidden="true">
                {isOpen ? '-' : '+'}
              </span>
            </button>
            <div
              id={panelId}
              role="region"
              aria-labelledby={triggerId}
              aria-hidden={!isOpen}
              className={`ContainerShopV2__accordionPanel ${isOpen ? 'ContainerShopV2__accordionPanel--open' : ''}`}
            >
              <div className="ContainerShopV2__accordionPanelInner">{renderGroupContent(group)}</div>
            </div>
          </ContainerShopV2>
        );
      })}
    </Fragment>
  );
};

export default GroupsShopV2;
